<template>
  <div id="AddressSearch" tabindex="-1" class="vld-parent elm-outline-none">
    <ValidationObserver ref="AddressSearch" v-slot="observer">
      <template v-if="!toggles.manualAddressEntry">
        <div class="flex flex-wrap mt-2 mb-2 -mx-3">
          <!-- Show post code entry if no addresses yet available -->
          <div v-if="addresses.length == 0" class="flex flex-col w-full px-3 mb-6 md:flex-row md:mb-0">
             
            <div v-if="!isAggregatorFlow" class="w-full mb-4 lg:mr-3 md:w-4/12 md:mr-4 lg:w-3/12 md:mb-0">
              <label class="font-semibold label" for="housenameornumber">House name or number</label>
              <input tabindex="0" id="houseNumber" v-model.trim="houseNumber" class="input focus:outline-none" type="text">
            </div>

            <div :class="{ 'w-full lg:mx-3 md:w-3/12': !isAggregatorFlow }">
              <ValidationProvider ref="postCode" name="Postcode" rules="min:1|max:32|postcode|required">
                <div slot-scope="{ errors }">
                  <!-- Post code -->
                  <label class="font-semibold label" for="postcode">Postcode</label>
                  <input tabindex="0" onkeyup="this.value = this.value.toUpperCase();" :class="{ 'border-error': errors[0] }" v-model.trim="postcode" class="input focus:outline-none" type="text">
                  <p class="mt-2 mb-4 text-polred">{{ errors[0] }}</p>
                </div>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <!-- We have addresses, show address selection -->
        <div class="mb-4" v-if="addresses.length > 0">
          <div class="flex mb-4">
            <!-- <div class="w-11/12 lg:w-4/12" v-if="!addressPreview">
              <label class="font-semibold label">House name or number</label>
              Choose below
            </div> -->
            <div class="w-full lg:w-6/12" :class="{'lg:w-10/12': isAggregatorFlow}" v-if="addressPreview">
              <h6 class="font-semibold">Address</h6>
              {{ addressPreview }}
            </div>
            <div class="w-5/12 lg:w-3/12" v-if="!addressPreview">
              <label class="font-semibold label">Post code</label>
              {{ uppercase(postcode) }}
            </div>
            <div class="w-1/3">
              <button tabindex="0" @keypress.enter.prevent="$event.target.click" type="button" @click="changeAddress(), changeAddressSetFocus()" class="block px-4 underline">
              Change
              </button>
            </div>
          </div>
          <div class="flex">
            <div class="w-2/3">
              <div class="w-full mt-2 mb-6" v-if="!addressPreview">
                <!-- Select an address-->
                <label class="font-semibold label" for="addresses">Select an address</label>
                <div class="relative inline-block">
                  <div class="fauxselect" @click="toggles.selectAddressOpen = !toggles.selectAddressOpen" :class="{
                    'fauxselected rounded-t-lg': toggles.selectAddressOpen,
                    'rounded-lg': !toggles.selectAddressOpen,
                    }" @keypress.enter.prevent="$event.target.click" tabindex="0">
                    <span>Choose address</span>
                  </div>
                  <div :class="{
                    'rounded-b-lg': toggles.selectAddressOpen
                    }" v-if="toggles.selectAddressOpen" class="fauxopts">
                    <div @keypress.enter.prevent="$event.target.click" tabindex="0" v-for="address in addresses" v-bind:key="extractAddressId(address)" @click="addressPreview = address.suggestion, selectedAddressId = extractAddressId(address), selectAddress(), changeAddressSetFocus(), selectOpen = false">
                      {{ address.suggestion }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="toggles.showTimeAtAddressSelection" class="mt-4">
            <label class="font-semibold label">How long have you lived here?</label>
            <div class="flex mt-6 mb-4">
              <div class="w-6/12 lg:w-3/12" :class="{'lg:w-5/12': isAggregatorFlow}">
                <div class="md:mb-0">
                  <label class="font-semibold label" for="years">Years</label>
                  <!-- Years at Address -->
                  <time-at-address :max="99" :model.sync="yearsAtAddress"/>
                </div>
              </div>
              <div class="w-6/12 ml-4 md:ml-10 lg:w-3/12" :class="{'lg:w-5/12': isAggregatorFlow}">
                <div class="md:mb-0">
                  <label class="font-semibold label" for="months">Months</label>
                  <!-- Months at Address -->
                  <time-at-address :min="id == 'previous' && yearsAtAddress > 0 ? 0 : id == 'current' ? 0 : 1" :max="11" :model.sync="monthsAtAddress"/>
                </div>
              </div>
            </div>
            <div class="mb-4" v-if="toggles.showNoTimeAtAddressError">
              <p class="text-polred">
                Please select years & months at address
              </p>
            </div>
          </div>
        </div>
      </template>
      <!-- Manual Address Entry -->
      <template v-if="toggles.manualAddressEntry">
        <div v-if="toggles.showMinSelectionErrorForManualEntry">
          <p class="text-polred"> Please provide one of the following: flat number, house name or house number.</p>
        </div>
        <div class="flex flex-wrap mt-2 mb-4 -mx-3">
          <div class="w-full px-3 md:w-1/4 md:mb-0">
            <label class="font-semibold label" for="flatnumber">Flat number</label>
            <!-- Manual : Flat Number -->
            <ValidationProvider vid="flatNumber" name="Flat number" rules="min:1|max:32">
              <div slot-scope="{ errors }">
                <input tabindex="0" :class="{ 'border-error': errors[0] || toggles.showMinSelectionErrorForManualEntry }" v-model.trim="manualAddressEntry.subBuilding1" class="input focus:outline-none" id="housenameornumber" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 md:w-1/4 md:mb-0">
            <label class="block mb-2 text-lg font-semibold" for="houseName">House name</label>
            <!-- Manual : House Name -->
            <ValidationProvider vid="houseName" name="House name" rules="min:1|max:32">
              <div slot-scope="{ errors }">
                <input autocomplete="off" tabindex="0" :class="{ 'border-error': errors[0] || toggles.showMinSelectionErrorForManualEntry }" v-model.trim="manualAddressEntry.building1" class="input focus:outline-none" id="houseName" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 md:w-1/4 md:mb-0">
            <label class="font-semibold label" for="houseNumber">House number</label>
            <!-- Manual : House Number -->
            <ValidationProvider vid="houseNumber" name="House number" rules="min:1|max:32">
              <div slot-scope="{ errors }">
                <input tabindex="0" :class="{ 'border-error': errors[0] || toggles.showMinSelectionErrorForManualEntry }" v-model.trim="manualAddressEntry.streetNumber1" class="input focus:outline-none" id="houseNumber" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 md:w-1/4">
            <label class="font-semibold label" for="streetname">Street name</label>
            <!-- Manual : Street Name -->
            <ValidationProvider vid="streetName" name="Street name" rules="min:1|max:40">
              <div slot-scope="{ errors }">
                <input tabindex="0" :class="{ 'border-error': errors[0] }" v-model.trim="manualAddressEntry.street1" class="input focus:outline-none" id="streetname" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 mt-4 md:w-1/4">
            <label class="font-semibold label" for="addressline2">Address line 2</label>
            <!-- Manual : Locality Name -->
            <input tabindex="0" v-model.trim="manualAddressEntry.locality1" class="input focus:outline-none" id="addressline2" type="text">
          </div>
          <div class="w-full px-3 mt-4 md:w-1/4">
            <ValidationProvider name="Town / City" rules="required|min:1|max:32">
              <div slot-scope="{ errors }">
                <label class="font-semibold label" for="towncity">Town / City</label>
                <!-- Manual : Town Name -->
                <input tabindex="0" :class="{ 'border-error': errors[0] }" v-model.trim="manualAddressEntry.townName" class="input focus:outline-none" id="towncity" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 mt-4 mb-6 md:w-1/4 md:mb-0">
            <ValidationProvider name="County" rules="required|min:1|max:32">
              <div slot-scope="{ errors }">
                <label class="font-semibold label" for="county">County</label>
                <!-- Manual : County Name -->
                <input tabindex="0" :class="{ 'border-error': errors[0] }" v-model.trim="manualAddressEntry.countyName" class="input focus:outline-none" id="county" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-3 mt-4 md:w-1/4">
            <ValidationProvider name="Postcode" rules="required|postcode">
              <div slot-scope="{ errors }">
                <label class="font-semibold label" for="postcode">Postcode</label>
                <!-- Manual : Post Code -->
                <input tabindex="0" onkeyup="this.value = this.value.toUpperCase();" :class="{ 'border-error': errors[0] }" v-model.trim="manualAddressEntry.postalCode1" class="input focus:outline-none" type="text">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <p class="block text-lg font-semibold">How long have you lived here?</p>
        <div class="flex flex-wrap mt-4 -mx-3" :class="{ 'mb-8': !toggles.showNoAddressTimeframeSelected, }">
          <div class="w-full px-3 md:w-2/4 lg:w-1/3">
            <label class="font-semibold label" for="years">Years</label>
            <!-- Manual : Years at Address -->
            <time-at-address :max="99" :model.sync="yearsAtAddress"></time-at-address>
          </div>
          <div class="w-full px-3 md:w-2/4 lg:w-1/3">
            <label class="font-semibold label" for="months">Months</label>
            <!-- Manual : Months at Address -->
            <time-at-address :min="yearsAtAddress > 0 ? 0 : 1" :max="11" :model.sync="monthsAtAddress"></time-at-address>
          </div>
        </div>
        <div class="mt-4 mb-8" v-if="toggles.showNoAddressTimeframeSelected">
          <p class="text-polred">
            Please select years & months at address
          </p>
        </div>
        <!-- Manual : Save Address -->
        <button type="button" @click="saveManualAddress()" class="px-4 py-2 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
        Save Address
        </button>
        <!-- Manual : Cancel Address Entry -->
        <button type="button" @click="toggles.manualAddressEntry = false" class="px-4 py-2 ml-3 font-semibold">
        Cancel manual entry
        </button>
      </template>
      <!-- Find Address -->
      <template v-if="!toggles.manualAddressEntry">
        <loading :active.sync="toggles.isLoading" :can-cancel="false" :is-full-page="false" :width="44" :height="44"/>
        <template v-if="!toggles.showTimeAtAddressSelection">
          <button type="button" @click="findAddresses(), changeAddressSetFocus()" class="px-8 py-2 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
          Find Address
          </button>
          <button type="button" @click="toggles.manualAddressEntry = true, changeAddressSetFocus()" class="px-0 md:px-4 py-2 ml-3 font-semibold underline">
            Enter manually
          </button>
          <div v-if="toggles.noResultsFound" class="inline py-4 text-center lg:px-4">
            <div class="flex items-center p-3 leading-none text-black lg:rounded-full lg:inline-flex" role="alert">
              <span class="flex-auto pl-3 mr-2 text-left">
              No address found, please try again
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <button type="button" @click="saveAddress(), changeAddressSetFocus()" class="px-4 py-2 font-semibold text-white rounded-full shadow bg-polred hover:bg-polred-dark">
            Save Address
          </button>
        </template>
      </template>
    </ValidationObserver>
  </div>
</template>

<script>

import Loading from 'vue-loading-overlay'
import TimeAtAddress from '@/v2/components/elements/TimeAtAddress'
import mixins from '@/v2/mixins'

export default {
  name: 'AddressSearch',
  mixins: [mixins],
  props: {
    id: String,
    isAggregatorFlow: Boolean
  },
  components: {
    Loading,
    TimeAtAddress
  },
  data() {
    return {
      addressPreview: '',
      apiBase: process.env.VUE_APP_ADDRESS_URL,
      toggles: {
        isLoading: false,
        noResultsFound: false,
        manualAddressEntry: false,
        selectAddressOpen: false,
        showTimeAtAddressSelection: false,
        showMinSelectionErrorForManualEntry: false,
        showNoTimeAtAddressError: false,
      },
      manualAddressEntry: {},
      yearsAtAddress: 0,
      monthsAtAddress: 0,
      houseNumber: '',
      postcode: '',
      addresses: [],
      addressEntry: {},
      selectedAddressId: 'Choose address',
    }
  },
  methods: {
    changeAddress() {
      this.addresses = []
      this.addressPreview = ''
      this.toggles.showTimeAtAddressSelection = false
      this.toggles.selectAddressOpen = false
    },
    async findAddresses() {

      if (!await this.validateField(this.$refs, 'postCode')) {
        return
      }

      this.toggles.isLoading = true

      let self = this

      let query = encodeURI(this.houseNumber + ' ' + this.postcode)

      let response = await this.axios.get(this.apiBase + 'search?query=' + query, {
        params: {
          country: 'GBR',
          take: 100,
          'auth-token': process.env.VUE_APP_ADDRESS_KEY,
      }})

      if (response) {
        this.toggles.isLoading = false
      }
      
      if (response.data.count > 0) {
        self.toggles.noResultsFound = false
        self.addresses = response.data.results
        return
      }

      // no results found
      alert('No address found, please enter your address manually')
      self.toggles.manualAddressEntry = true
      self.addresses = []
    },
    changeAddressSetFocus() {
      let focusChangeAddress = function(){
        if(document.getElementById("AddressSearch")){
          document.getElementById("AddressSearch").focus();
        }
        else {
            setTimeout(focusChangeAddress, 100);
        }
      }
      focusChangeAddress();
    },
    extractAddressId(addressObject) {
      return new URLSearchParams(addressObject.format).get('id')
    },
    async saveAddress() {

      this.toggles.showNoTimeAtAddressError = false

      if (!this.yearsAtAddress && !this.monthsAtAddress) {
        this.toggles.showNoTimeAtAddressError = true
        return
      }

      await this.$root.$emit(`${this.$options.name}:set`, {
        id: this.id,
        address: this.addressEntry,
        time: {
          years: this.yearsAtAddress,
          months: this.monthsAtAddress
        }
      })
    },
    selectAddress() {
      let self = this
      this.axios.get(this.apiBase + 'format', {
        params: {
          country: 'GBR',
          id: self.selectedAddressId,
          'auth-token': process.env.VUE_APP_ADDRESS_KEY
      }}).then(function(response) {
        self.toggles.showTimeAtAddressSelection = true
        self.addressEntry = response.data
      })
    },
    async saveManualAddress() {

      this.toggles.showMinSelectionErrorForManualEntry = false
      this.toggles.showNoAddressTimeframeSelected = false
      
      let errorFound = false

      if (!this.manualAddressEntry.subBuilding1 && !this.manualAddressEntry.streetNumber1 && !this.manualAddressEntry.building1) {
        this.toggles.showMinSelectionErrorForManualEntry = true
        errorFound = true
      }
      
      if (!this.yearsAtAddress && !this.monthsAtAddress) {
        this.toggles.showNoAddressTimeframeSelected = true
        errorFound = true
      }

      let isFormValid = await this.$refs[this.$options.name].validate()

      if (errorFound || !isFormValid) {
        return
      }

      this.toggles.manualAddressEntry = false
      
      this.$root.$emit(`${this.$options.name}:set`, {
        id: this.id,
        address: {
          components: this.manualAddressEntry
        },
        time: {
          years: this.yearsAtAddress,
          months: this.monthsAtAddress
        }
      })
    }
  }
}
</script>